<template>
  <nav class="nav-bar" :class="elClasses">
    <div class="nav-bar__top">
      <!-- Company's logo -->
      <RouterLink v-if="currentTeam" class="nav-bar__logo" to="/dashboard">
        <img
          :src="logoUrl"
          alt="logo"
          :style="logo.whiteBg ? 'background: #ffffff' : ''"
        >
      </RouterLink>
      <div v-if="currentTeam" class="nav-bar__menu-button" @click="showDrawerMenu">
        <div :class="`nav-bar__menu-button__burger${isMenuShown ? ' open' : ''}`">
          <span />
          <span />
          <span />
          <span />
        </div>
        <div v-if="isCustomExpanded || isExpanded" class="nav-bar__menu-button__text">
          {{ $t('message.MENU') }}
        </div>
      </div>
      <div class="nav-bar__title">
        <span v-if="(isCustomExpanded || isExpanded) && (navItems && navItems.length)">
          {{ $t("message.SHORTCUTS") }}
        </span>
        <span v-else-if="navItems && navItems.length">
          {{ $t("message.SHORTCUTS").substr(0, 3) }}
        </span>
      </div>
      <div class="nav-bar__list">
        <NavItem
          v-for="item in navItems"
          :key="item.id"
          :page-id="item.pageId"
          :page-options="item.pageOptions"
          :title="decodeHTML(item.title)"
          :to="item.link"
          :exact="item.exact"
          :img-src="item.imgSrc"
          :is-title-full="item.isTitleFull"
          :bg-color="item.bgColor"
          :path="item.path"
          :expanded="isCustomExpanded || isExpanded"
          min
          @click.native="onItemClicked"
        />
      </div>
    </div>

    <div :class="`nav-bar__bottom${isCustomExpanded || isExpanded ? ' nav-bar__bottom--expanded' : ''}`">
      <div
        v-if="currentTeam"
        :class="`ia-header-wrapper ${isCustomExpanded || isExpanded ? ' ia-header-wrapper--expanded' : ''}`"
        @click="$emit('iaClick')"
      >
        <img :src="`/images/ia/${neoPictureName}.png`" alt>
        <p v-if="isCustomExpanded || isExpanded">{{ $t('help.HELP') }}</p>
      </div>
      <div class="divider" />
      <Icon
        v-if="currentTeam"
        :class="`nav-bar__arrow${isCustomExpanded || isExpanded ? ' nav-bar__arrow--expanded' : ''}`"
        @click.native="isCustomExpanded = !isCustomExpanded"
      >
        {{ arrowDownThin }}
      </Icon>
      <div v-if="isExpanded || isCustomExpanded" class="nav-bar__bottom--container">
        <div v-if="!$store.state.device.isSmartphoneLandscape" class="nav-bar__bottom__date-hour--container">
          <p class="nav-bar__bottom__date-hour">{{ dateDisplayed }}</p>
          <p class="nav-bar__bottom__date-hour">{{ timeDisplayed }}</p>
        </div>
        <Icon
          v-if="fullscreenOption"
          class="nav-bar__fullscreen-img"
          @click.native="checkToggleFullscreen()"
        >
          {{ isFullscreen ? shrink : fullscreen }}
        </Icon>
      </div>
    </div>
  </nav>
</template>

<script>
/**
 * Navigation bar component.
 */

import { mapState } from 'vuex';
// Import components
import Icon from '@/components/icon.vue';
import NavItem from '@/components/nav-item.vue';
import mixinMeetingPages from '@/mixins/meeting-pages.vue';
// Import Vuex utils
import { GET_NAVIGATION_LIST } from '@/store/modules/navigation/mutation-types';
import { SET_ACTIVE_ROUTE_CLICKED, SET_ACTIVE_ROUTE_MATCHED, GET_LOGO } from '@/store/mutation-types';
// Import utils
import SPage from '@/utils/classes/SPage';
import { toggleFullscreen } from '@/utils/fullscreen';
import { NAV_LIST_DEFAULT, NAV_LIST_CONFIG_DEFAULT } from '@/utils/navigation-constants';
import { decodeHTML } from '@/utils/main';
import { arrowDownThin, fullscreen, shrink } from '@/assets/icons/';

const DEFAULT_LOGO_IMG = '/images/logo-full.png';
const DEFAULT_WHITE_LOGO_IMG = '/images/logo-full-white.png';
const DEFAULT_LOGO_WHEEL = '/images/logo300.png';

export default {

  name: 'NavBar',

  components: {
    Icon,
    NavItem,
  },

  mixins: [mixinMeetingPages],

  props: {
    config: Boolean,
    isMenuShown: Boolean,
    neoPictureName: {
      type: String,
      default: 'happy',
    },
  },

  data() {
    return {
      arrowDownThin,
      fullscreen,
      shrink,
      isMountedAfterDelay: false,
      isCustomExpanded: false,
      isFullscreen: false,
      dateIntervalId: null,
      currentDate: this.$moment(),
    };
  },

  computed: {
    /** Object: Classes applied to this.$el */
    elClasses() {
      return {
        // Prevent animating the width of the menu when loading the page
        'nav-bar--mounted': this.isMountedAfterDelay,
        'nav-bar--expanded': this.isCustomExpanded || this.isExpanded,
      };
    },
    /** Whether if the user is on mobile or not, and displays the reduced left menu */
    isExpanded() {
      if (this.currentTeam) {
        return !this.$store.state.device.isSmartphoneLandscape;
      }
      return false;
    },
    /** Whether we should display the fullscreen option or not */
    fullscreenOption() {
      // If loading in web app view, hide fullscreen option
      return window.matchMedia !== undefined
        || !window.matchMedia('(display-mode: standalone)').matches;
    },
    /** String: Current browser time */
    timeDisplayed() {
      return this.currentDate.format('HH:mm');
    },
    /** String: Date at the bottom left corner */
    dateDisplayed() {
      if (this.isCustomExpanded) {
        return this.currentDate.format(this.$t('monthDateFormat'));
      }
      return this.currentDate.format(this.$t('minDateFormat'));
    },
    navList() {
      if (this.$route.meta.loginPage) {
        return [];
      }
      if (this.$route.meta.requireConfig) {
        return NAV_LIST_CONFIG_DEFAULT;
      }
      const navList = this.$store.state.navigation[GET_NAVIGATION_LIST.stateKey];
      return (navList && navList.length ? navList : NAV_LIST_DEFAULT);
    },
    navItems() {
      return this.navList.map((item) => {
        const {
          pageId,
          pageOptions,
          id,
          name,
          isTitleFull,
          backgroundColor,
          itemId,
        } = item;
        const page = SPage.fromId[`${pageId}`];
        const newItem = {
          id,
          pageId,
          pageOptions: Array.isArray(pageOptions) ? {} : pageOptions,
          title: name,
          link: '/',
          isTitleFull,
          bgColor: backgroundColor,
        };
        if (page) {
          const defaultHomePage = (this.currentTeam && this.currentTeam.defaultHomePage)
            || { pageId: SPage.DASHBOARD.id };
          newItem.title = newItem.title || this.$t(page.i18nName);
          newItem.link = page.id === SPage.CFG_LEAVE.id
            ? this.getPageLink(defaultHomePage)
            : (this.getPageLink(item) || page.link);
          newItem.imgSrc = page.imgSrc;
          newItem.path = page.path;
          if (page.queryIdKey) {
            newItem.exact = { [page.queryIdKey]: itemId };
          }
        }
        if (!newItem.title) {
          newItem.title = this.$t('message.NONE');
        }
        return newItem;
      });
    },
    /** String: URL of the logo (or Shizen's logo) */
    logoUrl() {
      /* eslint-disable no-nested-ternary */
      const defaultLogo = this.isExpanded || this.isCustomExpanded
        ? (this.logo.whiteBg ? DEFAULT_LOGO_IMG : DEFAULT_WHITE_LOGO_IMG)
        : DEFAULT_LOGO_WHEEL;
      return this.logo.url || defaultLogo;
    },
    ...mapState({
      logo: state => state[GET_LOGO.stateKey] || {},
    }),
  },

  watch: {
    $route(to, from) {
      if (to.path !== from.path) {
        this.$store.commit(SET_ACTIVE_ROUTE_MATCHED, null);
      }
    },
  },

  created() {
    // Update current date
    this.dateIntervalId = setInterval(() => {
      this.currentDate = this.$moment();
    }, 5000);
    // Set current route as active
    this.$store.commit(SET_ACTIVE_ROUTE_CLICKED, this.$route);
  },

  mounted() {
    setTimeout(() => {
      this.isMountedAfterDelay = true;
    }, 2000);
  },

  beforeDestroy() {
    clearInterval(this.dateIntervalId);
  },

  methods: {
    decodeHTML,
    toggleFullscreen,
    checkToggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      toggleFullscreen();
    },

    onItemClicked() {
      if (this.$store.state.device.isSmartphoneLandscape) {
        this.isCustomExpanded = false;
      }
    },
    /** Tell th */
    showDrawerMenu(event) {
      if (!this.isMenuShown) {
        event.stopPropagation();
        this.$emit('showMenu');
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.nav-bar {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: $smartphone-nav-width;
  position: fixed;
  top: 0px;
  left: 0px;
  height: 100%;
  background: $color-shizen-blue;
  color: #fff;
  // This z-index is needed because of the mobile expansion behavior,
  // otherwise it will go under some elements (EX: Neo's help)
  z-index: 10017;
  padding-top: $nav-bar-top-padding;
  padding-left: $nav-bar-top-padding;
  box-sizing: border-box;

  &--mounted {
    // Prevent animating the width of the menu when loading the page
    transition: width 0.3s;
  }

  &--expanded {
    width: $nav-width-expanded;
  }

  &__logo {
    display: block;
    height: $nav-bar-logo-height;
    max-width: 100%;
    margin: $nav-bar-logo-margin 1.5rem 0 $nav-bar-logo-margin;

    & img {
      height: 100%;
      max-width: 100%;
      object-fit: contain;
    }
  }

  &__arrow {
    display: none;
    margin: 1em 0;
    width: 24px;
    cursor: pointer;
    transition: 0.3s transform;
    transform: rotate(-90deg);

    &--expanded {
      margin: 1em 0;
      transform: rotate(90deg);
    }
  }

  &__title {
    margin: $nav-bar-title-top-margin .9rem;
    line-height: $nav-bar-title-height;
    color: $color-shizen-light-blue-text;
    font-variant: small-caps;
  }

  &__top {
    flex: 1;
    width: 100%;
    flex-flow: column;
    min-height: 0px;
  }

  &__bottom {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    box-sizing: border-box;
    width: 100%;
    margin-bottom: 0.5em;
    padding: 2em 0.5em 0.5em;

    &--container {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    &--expanded {
      align-items: flex-start;
      padding: 2em 1em 0.5em;

      & .nav-bar__bottom__date-hour {
        font-size: 1em;
      }
    }
  }

  &__fullscreen-img {
    width: 1.2rem;
    height: 1.2rem;
    padding: .6rem;
    border-radius: $main-border-radius;
    cursor: pointer;
    display: block;
    margin: .5em 0;

    &:hover {
      background: $color-shizen-blue-btn;
    }
  }

  &__list {
    padding-right: $nav-bar-top-padding;
    min-height: 0px;
    overflow: auto;
    position: relative;
    height: calc(100% - #{$nav-bar-list-top-position});

    &::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0);
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.3);

      &:hover {
        background: rgba(255, 255, 255, 0.4);
      }

      &:active {
        background: rgba(255, 255, 255, 0.5);
      }
    }
  }

  &__menu-button {
    margin-top: $nav-bar-burger-top-margin;
    margin-left: 1rem;
    cursor: pointer;
    display: flex;
    align-items: center;

    & > p {
      color: white;
    }

    &__burger {
      width: 1.1rem;
      height: $nav-bar-burger-height;
      position: relative;
      margin: 6px 0.5em 6px 0;
      transform: rotate(0deg);
      transition: transform 0.5s ease-in-out;
      cursor: pointer;

      &.open span:nth-child(1) {
        top: 0;
        width: 0%;
        left: 50%;
      }

      &.open span:nth-child(2) {
        transform: rotate(45deg);
      }

      &.open span:nth-child(3) {
        transform: rotate(-45deg);
      }

      &.open span:nth-child(4) {
        bottom: 0;
        width: 0%;
        left: 50%;
      }

      & span {
        display: block;
        position: absolute;
        height: 1px;
        width: 100%;
        background: white;
        border-radius: 9px;
        opacity: 1;
        left: 0;
        transform: rotate(0deg);
        transition: transform .25s ease-in-out;

        &:nth-child(1) {
          top: 0px;
        }

        &:nth-child(2), &:nth-child(3) {
          top: calc(50% - 0.5px);
        }

        &:nth-child(4) {
          bottom: 0;
        }
      }

      &__item {
        width: 24px;
        height: 2px;
        background: white;

        &:nth-of-type(2) {
          margin: 6px 0;
        }
      }
    }
  }

  .ps {
    &__rail-y:hover {
      background: transparent;

      .ps__thumb-y {
        width: 6px;
      }
    }
  }
}

.ia-header-wrapper {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  cursor: pointer;

  &--expanded {
    margin-right: 0.5em;
  }

  img {
    width: 1.35rem;
    display: block;
    height: 100%;
    margin-right: 0.9rem;
  }
}

.divider {
  height: 2px;
  width: 100%;
  margin: 1em 0;
  background: $color-border-grey-dark;
}

/* Most likely mobile with keyboard opened */
@media (max-height: 400px) {
 .nav-bar__bottom {
    display: none;
  }
}

@media (max-width: $smartphone-landscape) {
  .nav-bar {
    $padding-top: 3px;

    overflow: hidden;
    width: $smartphone-nav-width;
    padding: $padding-top;

    &__title {
      margin-left: 0;
      margin-right: 0;
      padding: 0;
      text-align: center;
    }

    &__list {
      padding-right: 0;
    }

    &__arrow {
      display: block;
    }

    &__menu-button {
      margin-top: 2.5rem;
      margin-left: 0;
      padding: .5rem 0;

      &__burger {
        margin: auto;
      }
    }

    &__bottom {
      display: flex;
      &__indicators {
        justify-content: flex-start;
      }
    }

    &__logo {
      height: 2.2rem;
      margin-left: .2rem;
      margin-top: .4rem;
      margin-right: .2rem;
    }

    &__fullscreen-img {
      width: 20px;
      margin-bottom: 5px;
    }

    .ia-header-wrapper {
      text-align: center;

      img {
        margin: 0;
      }
    }

    &--expanded {
      width: $nav-width-expanded;

      .nav-bar {
        &__menu-button {
          &__burger {
            margin: 0 1rem;
          }
        }

        &__title {
          text-align: left;
          margin-left: 1rem;
        }
      }


      .ia-header-wrapper {
        img {
          margin-right: 0.5rem;
        }
      }
    }
  }
}
</style>
